<template>
  <!-- prettier-ignore -->
  <div class="actions">
    <div class="actions__left">
  		<basic-text-button
        v-if="!$props.hideDetails"
        class="link"
        :label="$t('product.more')"
        @click="$emit('open-detail')"
      >
        <template #trailing>
          <the-icon
            art="solid"
            name="arrow-right"
          />
        </template>
      </basic-text-button>
    </div>

    <div class="actions__right">
  		<basic-button
        v-if="$props.productState === PRODUCT_STATE.EDITABLE && ($props.product.selected || $props.product.valid)"
        hierarchy="secondary"
        :label="titleLabel"
        @click="actionHandler"
      />

      <template v-else>
        <div>&nbsp;</div>
      </template>
    </div>
	</div>
</template>

<script setup>
  import { computed } from 'vue'

  import useI18n from '@shared/hooks/useI18n'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'

  import { BasicButton, BasicTextButton } from '@shared/components/Basic'

  import { PRODUCT_STATE } from '@shared/config/constants'

  // HOOKS
  const { t, te } = useI18n()

  // INIT
  const emit = defineEmits(['add', 'open-detail', 'remove', 'search'])
  const props = defineProps({
    hideDetails: {
      type: Boolean,
      default: false,
    },

    personId: {
      type: String,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    productState: {
      type: String,
      required: true,
    },

    isDoctorSearchSkipped: {
      type: Boolean,
      default: false
    }
  })

  // COMPUTED
  /**
   * Only show search doctor if the product is not selected.
   * If it's selected and the doctor is missing,
   * a warning message and action will be displayed in the product doctor.
   */
  const needsDoctor = computed(() => {
    return !props.isDoctorSearchSkipped && props.product.medicalOffice && !props.product.doctor && !props.product.selected
  })

  const titleLabel = computed(() => {
    if (props.product.selected) {
      return getContent(props.product.productId, 'actions.remove')
    } else {
      return getContent(props.product.productId, 'actions.add')
    }
  })

  const willReplaceIds = computed(() => {
    const { willReplace } = usePersonSpecificProduct()

    return willReplace(props.personId, props.product.productId)
  })

  // METHODS
  function actionHandler() {
    if (needsDoctor.value) {
      emit('search')
    } else if (!props.product.selected && props.product.valid) {
      const productId = willReplaceIds.value
      emit('add', { replace: !!productId, productId })
    } else if (props.product.selected) {
      emit('remove')
    }
  }

  function getContent(productId, key) {
    const productPath = `products.${productId}`
    return te(`${productPath}.${key}`) ? t(`${productPath}.${key}`) : t(`product.${key}`)
  }
</script>

<style scoped>
.actions {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  column-gap: var(--fixed-spacing-fix-02);
  row-gap: var(--fixed-spacing-fix-04);

  &__left {
    text-align: left;
  }

  &__right {
    text-align: right;
  }

  &__unavailable {
    .icon {
      color: var(--primary);
    }
  }
}

@media (--v-medium) {
  .actions {
    row-gap: 8px;
  }
}

@media (--v-large) {
}

@media (--v-wide) {
  .actions {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

@media (--v-ultra) {
}

@media (--v-max) {
}
</style>
